import { isEmpty, isNil, partition } from 'lodash';
import { useMemo, useState } from 'react';
import { Menu } from '@shared/components/Menu';
import { View } from '@shared/components/View';
import { LoadingIndicator } from '@shared/components/loading';
import { MBSearchBar } from '@shared/components/searchBar';
import { useChannelMembersList } from '@shared/scenes/channel/userList/hooks/useChannelMembersList';
import { SeenByList } from '@shared/scenes/chat/thread/components/seenBy/SeenByList';
import { useThreadContext } from '@shared/scenes/chat/thread/containers/thread/provider';
import { API } from '@shared/types/api';
import { useChannelId } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';

interface Props {
  users: API.ShortUserFragment[];
  sender?: API.ShortUserFragment;
}

export function SeenByMenuContent({ users, sender }: Props) {
  const [keyword, setKeyword] = useState<string>('');
  const { channelSlug } = useThreadContext();
  const channelId = useChannelId(channelSlug);
  const loadingChannel = isNil(channelId);
  const {
    users: channelUsers,
    hasMore,
    loading: loadingChannelUsers,
  } = useChannelMembersList({ channelId });
  const loading = loadingChannel || loadingChannelUsers;

  const notSeen = useMemo(() => {
    const [, notSeenUsers] = partition(
      channelUsers.filter(user => !user.isMe && user.id !== sender?.id),
      user => users.some(u => u.id === user.id),
    );
    return notSeenUsers;
  }, [channelUsers, sender?.id, users]);

  const hasChannelMembers = !isEmpty(channelUsers);
  const showLoading = !hasChannelMembers && loading;
  const showList = hasChannelMembers || !loading;

  return (
    <View flex>
      {hasMore && <Menu.Note>{i18n('chat.seenBySheet.largeWorkspaceNote')}</Menu.Note>}
      <Menu.Section>
        <MBSearchBar
          value={keyword}
          onChangeText={setKeyword}
          placeholder={i18n('common.search')}
          returnKeyType="search"
          autoFocus={true}
        />
      </Menu.Section>
      {showLoading && <LoadingIndicator fill padding={4} />}
      {showList && (
        <SeenByList seen={users} notSeen={notSeen} keyword={keyword} hasMoreMembers={hasMore} />
      )}
    </View>
  );
}
